import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Save, Store, Receipt, Eye, Upload, Trash2 } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import InvoicePreviewDialog from "@/components/InvoicePreviewDialog";

const empty = {
  name: "",
  address: "",
  phone: "",
  gstin: "",
  email: "",
  license_no: "",
  bill_prefix: "INV",
  bill_footer: "*** Thank You — Get Well Soon ***",
  bill_terms: "Goods once sold cannot be returned.",
  show_gstin_on_bill: true,
  tax_applicable: true,
  logo_data_url: "",
  thermal_paper_width: "72mm",
  default_gst_percent: 12,
};

const sampleBill = {
  bill_no: "INV-PREVIEW-0001",
  created_at: new Date().toISOString(),
  customer_name: "Sample Customer",
  customer_phone: "9876543210",
  items: [
    { medicine_name: "Paracetamol 500mg", batch_no: "P123", hsn_code: "3004", quantity: 2, mrp: 25.5, gst_percent: 12, discount_percent: 0 },
    { medicine_name: "Dolo 650mg", batch_no: "D45", hsn_code: "3004", quantity: 1, mrp: 32, gst_percent: 12, discount_percent: 5 },
  ],
  subtotal: 83,
  total_discount: 1.6,
  taxable_amount: 72.68,
  cgst: 4.36,
  sgst: 4.36,
  total_tax: 8.72,
  grand_total: 81.4,
  payment_mode: "Cash",
};

export default function Settings() {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    api.get("/settings/shop").then((r) => setForm({ ...empty, ...r.data }));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const safePaperWidth = ["58mm", "72mm", "80mm"].includes(form.thermal_paper_width) ? form.thermal_paper_width : "72mm";

  const save = async () => {
    if (!form.name.trim()) return toast.error("Shop name is required");
    setSaving(true);
    try {
      await api.put("/settings/shop", form);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail === "Admin only" ? "Only admins can edit settings" : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Configuration</div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: "Chivo" }}>Settings</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setPreviewOpen(true)} data-testid="preview-bill-btn">
            <Eye size={14} className="mr-1" /> Preview Sample Bill
          </Button>
          <Button onClick={save} disabled={saving} data-testid="save-settings-btn">
            <Save size={14} className="mr-1" /> {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-border rounded-md shadow-none">
          <CardHeader className="border-b border-border">
            <CardTitle className="flex items-center gap-2 text-base">
              <Store size={16} className="text-primary" /> Pharmacy Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-3">
            <F label="Pharmacy Name *">
              <Input data-testid="setting-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
            </F>
            <F label="Address">
              <Textarea rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="setting-address" />
            </F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Phone">
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="setting-phone" />
              </F>
              <F label="Email">
                <Input value={form.email} onChange={(e) => set("email", e.target.value)} />
              </F>
              <F label="GST Number">
                <Input value={form.gstin} onChange={(e) => set("gstin", e.target.value)} data-testid="setting-gstin" />
              </F>
              <F label="Drug License No.">
                <Input value={form.license_no} onChange={(e) => set("license_no", e.target.value)} />
              </F>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border rounded-md shadow-none">
          <CardHeader className="border-b border-border">
            <CardTitle className="flex items-center gap-2 text-base">
              <Receipt size={16} className="text-primary" /> Bill Setup
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <F label="Bill Prefix" hint="e.g. INV, BILL, RX">
                <Input value={form.bill_prefix} onChange={(e) => set("bill_prefix", e.target.value.toUpperCase())} data-testid="setting-prefix" />
              </F>
              <F label="Sample Bill No." hint="auto-generated">
                <Input readOnly disabled value={`${form.bill_prefix || "INV"}-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}-0001`} className="font-mono" />
              </F>
            </div>
            <F label="Receipt Footer Text" hint="Shown at the bottom of every receipt">
              <Input value={form.bill_footer} onChange={(e) => set("bill_footer", e.target.value)} data-testid="setting-footer" />
            </F>
            <F label="Terms Line" hint="Small print below footer">
              <Input value={form.bill_terms} onChange={(e) => set("bill_terms", e.target.value)} />
            </F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Thermal Paper Width" hint="Used by receipt preview and print">
                <select
                  value={safePaperWidth}
                  onChange={(e) => set("thermal_paper_width", e.target.value)}
                  data-testid="setting-paper-width"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="58mm">58mm</option>
                  <option value="72mm">72mm</option>
                  <option value="80mm">80mm</option>
                </select>
              </F>
              <F label="Default GST %" hint="Saved as shop default only">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={form.default_gst_percent ?? 12}
                  onChange={(e) => set("default_gst_percent", e.target.value === "" ? "" : Number(e.target.value))}
                  data-testid="setting-default-gst"
                />
              </F>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
              <div>
                <div className="text-sm font-medium">Show GSTIN on Bill</div>
                <div className="text-xs text-muted-foreground">Prints your GSTIN on every invoice header</div>
              </div>
              <Switch
                checked={!!form.show_gstin_on_bill}
                onCheckedChange={(v) => set("show_gstin_on_bill", v)}
                data-testid="setting-show-gstin"
              />
            </div>

            <div className="rounded-md border border-border p-3">
              <div className="text-sm font-medium mb-1">GST / Tax Applicable?</div>
              <div className="text-xs text-muted-foreground mb-3">
                Turn off to bill without any tax computation. Bills will show only the total price without CGST / SGST breakup.
              </div>
              <RadioGroup
                value={form.tax_applicable ? "yes" : "no"}
                onValueChange={(v) => set("tax_applicable", v === "yes")}
                className="flex gap-6"
                data-testid="setting-tax-applicable"
              >
                <label className="flex items-center gap-2 cursor-pointer" data-testid="tax-yes">
                  <RadioGroupItem value="yes" id="tax-yes" />
                  <span className="text-sm">Yes — apply GST (CGST + SGST)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="tax-no">
                  <RadioGroupItem value="no" id="tax-no" />
                  <span className="text-sm">No — bill without tax</span>
                </label>
              </RadioGroup>
            </div>

            <div className="rounded-md border border-border p-3">
              <div className="text-sm font-medium mb-1">Shop Logo on Bill</div>
              <div className="text-xs text-muted-foreground mb-3">
                Uploads as monochrome-friendly PNG. Prints at the top of every configured thermal receipt.
              </div>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 border border-border rounded-md grid place-items-center bg-muted overflow-hidden">
                  {form.logo_data_url ? (
                    <img src={form.logo_data_url} alt="logo" className="max-w-full max-h-full" data-testid="logo-preview" />
                  ) : (
                    <span className="text-[11px] text-muted-foreground">No logo</span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="inline-flex">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => onLogoPick(e.target.files?.[0])}
                      data-testid="logo-input"
                    />
                    <span className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-border bg-white hover:bg-muted text-sm cursor-pointer">
                      <Upload size={14} /> {form.logo_data_url ? "Replace logo" : "Upload logo"}
                    </span>
                  </label>
                  {form.logo_data_url && (
                    <button
                      onClick={() => set("logo_data_url", "")}
                      data-testid="logo-remove"
                      className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-border bg-white hover:bg-muted text-sm text-destructive"
                    >
                      <Trash2 size={14} /> Remove
                    </button>
                  )}
                </div>
              </div>
              <div className="text-[11px] text-muted-foreground mt-2">
                Recommended: 300×120 PNG on white background. Max 2 MB — auto-resized to 240px wide on upload.
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <InvoicePreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        bill={sampleBill}
        shop={form}
      />
    </div>
  );
}

function F({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}
